import React, { useState, useMemo, useRef } from 'react';
import { getTeamStyle } from '../utils/teamColors';
import { useSeason } from '../context/SeasonContext';
import { useStandings } from '../hooks/useStandings';
import { toPng } from 'html-to-image';

// ── Layout constants (mirrors PlayoffBracket.jsx geometry) ──────────────────
const TEAM_ROW = 28;       // height of one team slot
//const VS_GAP   = 10;       // height of the "vs" divider
const MATCH_H  = 60;   // 92 — total matchup block height
//const R1_GAP   = 0;       // vertical gap between R1 matchups
const R1_UNIT  = 40;        // 108 — repeating unit

const CANVAS_H = 848;  // 848
const COL_W    = 190;      // each round column width
const PIPE_W   = 36;       // SVG connector column width

const r1Top = (i) => i * R1_UNIT;
const r1Cy  = (i) => r1Top(i) + MATCH_H / 2;

const roundCy = (round, slot) => {
  if (round === 0) return r1Cy(slot);
  const span = Math.pow(2, round);
  const firstFeeder = slot * span;
  return (r1Cy(firstFeeder) + r1Cy(firstFeeder + span - 1)) / 2;
};

const roundTop = (round, slot) => roundCy(round, slot) - MATCH_H / 2;

// ── Seeding order — identical pairing to the official PlayoffBracket ────────
const R1_ORDER = [
  { hi: 1, lo: 16 }, { hi: 8, lo: 9 },
  { hi: 4, lo: 13 }, { hi: 5, lo: 12 },
  { hi: 3, lo: 14 }, { hi: 6, lo: 11 },
  { hi: 2, lo: 15 }, { hi: 7, lo: 10 },
];

// ── Connector lines between rounds ───────────────────────────────────────────
const ConnectorSVG = ({ pairs, color = '#d1d5db' }) => (
  <svg width={PIPE_W} height={CANVAS_H} className="flex-none" style={{ overflow: 'visible' }}>
    {pairs.map(({ fromA, fromB, to }, i) => {
      const midX = PIPE_W / 2;
      return (
        <g key={i}>
          <line x1={0} y1={fromA + MATCH_H/2} x2={midX} y2={fromA + MATCH_H/2} stroke={color} strokeWidth={1.5} />
          <line x1={0} y1={fromB + MATCH_H/2} x2={midX} y2={fromB + MATCH_H/2} stroke={color} strokeWidth={1.5} />
          <line x1={midX} y1={fromA + MATCH_H/2} x2={midX} y2={fromB + MATCH_H/2} stroke={color} strokeWidth={1.5} />
          <line x1={midX} y1={to + MATCH_H/2} x2={PIPE_W} y2={to + MATCH_H/2} stroke={color} strokeWidth={1.5} />
        </g>
      );
    })}
  </svg>
);

// ── Pickable team row ─────────────────────────────────────────────────────────
const PickSlot = ({ team, seed, isPicked, isLocked, onPick }) => {
  if (!team) {
    return (
      <div
        className="flex items-center px-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30"
        style={{ height: TEAM_ROW }}
      >
        <span className="text-xs text-gray-500 dark:text-gray-400 italic">Waiting on pick...</span>
      </div>
    );
  }

  const ts = team ? getTeamStyle(team) : null;
  //const seedColor = team ? getReadableTeamColor(team, getTeamStyle(team).primaryHex, isDarkModeActive()) : '#9ca3af';

  return (
    <button
      type="button"
      onClick={() => onPick(team.id)}
      disabled={isLocked}
      className={`w-full flex items-center gap-2 px-3 rounded-lg border-2 transition-all duration-150 text-left
        ${isPicked
          ? 'border-transparent shadow-md scale-[1.02]'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:scale-[1.01]'}
        ${isLocked && !isPicked ? 'opacity-40' : ''}
        ${!isLocked ? 'cursor-pointer active:scale-95' : 'cursor-default'}`}
      style={{ height: TEAM_ROW, ...(isPicked ? ts.accentStyle : {}) }}
    >
      <div
        className="flex-none flex items-center justify-center rounded text-[10px] font-black"
        style={{
          width: 20, height: 20,
          color: isPicked ? '#fff' : '#9ca3af',
          background: isPicked ? 'rgba(255,255,255,0.25)' : undefined,
        }}
      >
        {seed ?? ''}
      </div>

      {team.logo_url ? (
        <img src={team.logo_url} alt={team.name} className="flex-none w-6 h-6 rounded object-cover" />
      ) : (
        <div
          className="flex-none w-6 h-6 rounded flex items-center justify-center text-[10px] font-black text-white"
          style={isPicked ? { background: 'rgba(255,255,255,0.25)' } : ts.headerStyle}
        >
          {team.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
        </div>
      )}

      <span className={`flex-1 min-w-0 text-sm font-bold truncate ${isPicked ? 'text-white' : 'text-gray-800 dark:text-gray-200'}`}>
        {team.name}
      </span>

      {isPicked && (
        <svg className="flex-none w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
};

// ── Matchup block ─────────────────────────────────────────────────────────────
const PredictionMatchup = ({ topTeam, bottomTeam, topSeed, bottomSeed, pickedId, onPick }) => {
  const bothPresent = !!topTeam && !!bottomTeam;

  return (
    <div
      className="flex flex-col justify-between p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/40"
      style={{ height: MATCH_H+15 }}
    >
      <PickSlot
        team={topTeam}
        seed={topSeed}
        isPicked={!!topTeam && pickedId === String(topTeam.id)}
        isLocked={!bothPresent}
        onPick={onPick}
      />
      <div className="flex items-center justify-center select-none">
        <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">vs</span>
      </div>
      <PickSlot
        team={bottomTeam}
        seed={bottomSeed}
        isPicked={!!bottomTeam && pickedId === String(bottomTeam.id)}
        isLocked={!bothPresent}
        onPick={onPick}
      />
    </div>
  );
};

// ── Round column ───────────────────────────────────────────────────────────────
const RoundColumn = ({ round, matchups, onPick }) => (
  <div className="relative flex-none" style={{ width: COL_W, height: CANVAS_H }}>
    {matchups.map((m, slot) => (
      <div key={slot} className="absolute left-0 right-0" style={{ top: roundTop(round, slot) }}>
        <PredictionMatchup {...m} onPick={(teamId) => onPick(round, slot, teamId)} />
      </div>
    ))}
  </div>
);

// ── Champion banner ───────────────────────────────────────────────────────────
const ChampionBanner = ({ team }) => {
  if (!team) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: TEAM_ROW }}>
        <div className="flex items-center gap-2 px-4 py-1.5 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <span className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
            Your Champion
          </span>
        </div>
      </div>
    );
  }

  const ts = team ? getTeamStyle(team) : null;
  //const seedColor = team ? getReadableTeamColor(team, getTeamStyle(team).primaryHex, isDarkModeActive()) : '#9ca3af';
  
  return (
    <div className="flex flex-col items-center justify-center gap-2 transition-all duration-300">
      <span className="text-2xl select-none">🏆</span>
      <div
        className="flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg"
        style={ts.accentStyle}
      >
        {team.logo_url ? (
          <img src={team.logo_url} alt={team.name} className="w-7 h-7 rounded object-cover" />
        ) : (
          <div className="w-7 h-7 rounded flex items-center justify-center text-[10px] font-black text-white" style={ts.headerStyle}>
            {team.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
        )}
        <span className="text-sm font-black uppercase tracking-wide text-white">{team.name}</span>
      </div>
    </div>
  );
};

// ── Main PlayoffPredictions page ──────────────────────────────────────────────
// teams: same standings-ordered array Standings.jsx already fetches via useStandings.
// No server reads/writes — picks live only in component state for the session.
export default function PlayoffPredictions () {
  // picks[round][slot] = teamId picked to win that matchup
  // round 1 = QF picks (made by clicking in R1 matchups)
  // round 2 = SF picks (made by clicking in QF matchups)
  // round 3 = Final picks (made by clicking in SF matchups)
  // round 4 = champion pick (made by clicking in the Final matchup) — single slot 0

  const { activeSeason } = useSeason();
  const { data: teams, loading } = useStandings(activeSeason?.id);
  const [picks, setPicks] = useState({ 1: {}, 2: {}, 3: {}, 4: {} });
  const [bracketName, setBracketName] = useState('');
  const bracketRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Download Handler Function
  const handleDownloadImage = async () => {
    if (!bracketRef.current) return;
    setIsDownloading(true);

    try {
      // Configures canvas conversion to support absolute SVG connectors and styling properly
      const dataUrl = await toPng(bracketRef.current, {
        quality: 0.95,
        backgroundColor: document.documentElement.classList.contains('dark') ? '#0f172a' : '#ffffff', // Ensures correct theme background color
        style: {
          transform: 'scale(1)', // Prevents zoom/viewport glitches
          padding: '24px', // breathing room around the bracket
        },
      });

      // Creates a temporary anchor element to trigger the browser's download prompt
      const link = document.createElement('a');
      const filename = bracketName.trim() 
        ? `${bracketName.trim().toLowerCase().replace(/\s+/g, '-')}-bracket.png`
        : 'playoff-bracket.png';
      
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Oops, something went wrong with the screenshot!', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const preparedTeams = useMemo(() => (teams || []).map(team => {
    const profile = team?.team_profiles?.[0] || team?.team_profile || {};
    return {
      ...team,
      primary_color: profile.primary_color || team.primary_color,
      secondary_color: profile.secondary_color || team.secondary_color,
      gradient: profile.gradient ?? team.gradient ?? false,
    };
  }), [teams]);

  const seeded = useMemo(() => {
    const arr = [...preparedTeams].slice(0, 16);
    while (arr.length < 16) arr.push(null);
    return arr;
  }, [preparedTeams]);

  const teamsById = useMemo(
    () => preparedTeams.reduce((acc, t) => { acc[t.id] = t; return acc; }, {}),
    [preparedTeams]
  );

  // Round 1 (R1) is fixed seeding from current standings — not a user pick.
  const r1 = useMemo(() => R1_ORDER.map(({ hi, lo }) => ({
    topTeam: seeded[hi - 1] || null,
    bottomTeam: seeded[lo - 1] || null,
    topSeed: hi,
    bottomSeed: lo,
  })), [seeded]);

  // Clears any picks downstream of a given (round, slot) pick — handles the
  // "changed my mind" case so a stale winner doesn't linger further into the bracket.
  const clearDownstream = (allPicks, round, slot) => {
    let r = round + 1;
    let s = Math.floor(slot / 2);
    while (r <= 4) {
      if (allPicks[r]?.[s] != null) {
        allPicks[r] = { ...allPicks[r] };
        delete allPicks[r][s];
      }
      s = Math.floor(s / 2);
      r += 1;
    }
  };

const handlePick = (round, slot, teamId) => {
  setPicks(prev => {
    // Dynamically clone the exact round object, ensuring it exists
    const next = {
      ...prev,
      [round]: prev[round] ? { ...prev[round] } : {}
    };

    next[round][slot] = String(teamId);
    clearDownstream(next, round, slot);
    return next;
  });
};


  const resetPicks = () => setPicks({ 1: {}, 2: {}, 3: {}, 4: {} });

  // Resolves a matchup's seed label for a picked team by checking which feeder slot it came from
  const seedFor = (feeder, teamId) => {
    if (!feeder || !teamId) return null;
    if (feeder.topTeam?.id === teamId) return feeder.topSeed;
    if (feeder.bottomTeam?.id === teamId) return feeder.bottomSeed;
    return null;
  };

  // Build QF matchups from R1 picks
  const qf = Array.from({ length: 4 }, (_, slot) => {
    const topFeeder = r1[2 * slot];
    const bottomFeeder = r1[2 * slot + 1];
    const topId = picks[1][2 * slot] ?? null;
    const bottomId = picks[1][2 * slot + 1] ?? null;
    return {
      topTeam: topId ? teamsById[topId] : null,
      bottomTeam: bottomId ? teamsById[bottomId] : null,
      topSeed: seedFor(topFeeder, topId),
      bottomSeed: seedFor(bottomFeeder, bottomId),
      pickedId: null,
    };
  });

  // Build SF matchups from QF picks
  const sf = Array.from({ length: 2 }, (_, slot) => {
    const topFeeder = qf[2 * slot];
    const bottomFeeder = qf[2 * slot + 1];
    const topId = picks[2][2 * slot] ?? null;
    const bottomId = picks[2][2 * slot + 1] ?? null;
    return {
      topTeam: topId ? teamsById[topId] : null,
      bottomTeam: bottomId ? teamsById[bottomId] : null,
      topSeed: seedFor(topFeeder, topId),
      bottomSeed: seedFor(bottomFeeder, bottomId),
      pickedId: null,
    };
  });

  // Build the Final from SF picks
  const finalTopId = picks[3][0] ?? null;
  const finalBottomId = picks[3][1] ?? null;
  const final = {
    topTeam: finalTopId ? teamsById[finalTopId] : null,
    bottomTeam: finalBottomId ? teamsById[finalBottomId] : null,
    topSeed: seedFor(sf[0], finalTopId),
    bottomSeed: seedFor(sf[1], finalBottomId),
  };

  // Champion = the Final pick (round 4, slot 0)
  const championId = picks[4][0] ?? null;
  const champion = championId ? teamsById[championId] : null;

  // Connector geometry between rounds — same shape as the official bracket
  const r1ToQf = Array.from({ length: 4 }, (_, i) => ({
    fromA: roundCy(1, 2 * i), fromB: roundCy(1, 2 * i + 1), to: roundCy(2, i),
  }));
  const qfToSf = Array.from({ length: 2 }, (_, i) => ({
    fromA: roundCy(2, 2 * i), fromB: roundCy(2, 2 * i + 1), to: roundCy(3, i),
  }));
  // The Final sits at the true midpoint between the two Semifinal games.
  const finalCy = (roundCy(3, 0) + roundCy(3, 1)) / 2;
  const sfToF = [{ fromA: roundCy(3, 0), fromB: roundCy(3, 1), to: finalCy }];

  const totalPicks =
    Object.keys(picks[1]).length +
    Object.keys(picks[2]).length +
    Object.keys(picks[3]).length +
    Object.keys(picks[4]).length;
  const totalNeeded = 8 + 4 + 2 + 1; // R1 winners + QF winners + SF winners + champion

    if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
      {/* Header */}
      <div className="bg-orange-600 dark:bg-gray-900 p-6">
        <h1 className="text-3xl font-black italic uppercase tracking-tighter text-white">
          Make Your Picks
        </h1>
        <p className="text-gray-100 dark:text-gray-400 text-sm font-medium">
          Pick every winner through the Finals, then download to share
        </p>
      </div>
        <div className="text-center py-20 text-gray-400 dark:text-gray-500 animate-pulse text-sm">
          Loading round 1...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
      {/* Header */}
      <div className="bg-orange-600 dark:bg-gray-900 p-6">
        <h1 className="text-3xl font-black italic uppercase tracking-tighter text-white">
          Make Your Picks
        </h1>
        <p className="text-gray-100 dark:text-gray-400 text-sm font-medium">
          Pick every winner through the Finals, then download to share
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30">
        <input
          type="text"
          value={bracketName}
          onChange={(e) => setBracketName(e.target.value)}
          placeholder="Your name (optional, shows on screenshot)"
          maxLength={40}
          className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <span className="text-xs font-bold text-gray-400 dark:text-gray-500">
          {totalPicks} / {totalNeeded} picks made
        </span>
        <button
          type="button"
          onClick={resetPicks}
          className="px-3 py-2 rounded-lg text-xs font-bold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          Reset bracket
        </button>

        {/* New Download Button */}
        <button 
          type="button" 
          onClick={handleDownloadImage}
          disabled={isDownloading}
          className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 dark:bg-orange-500 dark:hover:bg-orange-600 transition-colors shadow-md flex items-center gap-1"
        >
          {isDownloading ? (
            <>
              <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : 'Download Bracket (PNG)'}
        </button>
      </div>

      {bracketName.trim() && (
        <div className="px-6 pt-4 text-sm font-bold text-gray-600 dark:text-gray-300">
          {bracketName.trim()}'s Bracket
        </div>
        )}

      {/* Bracket */}
      <div className="p-4 md:p-6 overflow-x-auto pb-4">
        <div
          ref={bracketRef}
          className="relative flex items-center mx-auto w-fit"
        >
          {/* Round 1 */}
          <div className="flex-none flex flex-col" style={{ width: COL_W, height: CANVAS_H }}>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Round 1
            </p>
            <RoundColumn round={1} matchups={r1} onPick={handlePick} />
          </div>

          <ConnectorSVG pairs={r1ToQf} />

          {/* Quarterfinals */}
          <div className="flex-none flex flex-col" style={{ width: COL_W, height: CANVAS_H }}>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Quarterfinals
            </p>
            <RoundColumn round={2} matchups={qf} onPick={handlePick} />
          </div>

          <ConnectorSVG pairs={qfToSf} />

          {/* Semifinals */}
          <div className="flex-none flex flex-col" style={{ width: COL_W, height: CANVAS_H }}>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Semifinals
            </p>
            <RoundColumn round={3} matchups={sf} onPick={handlePick} />
          </div>

          <ConnectorSVG pairs={sfToF} />

          {/* Finals */}
          <div className="relative flex-none flex flex-col" style={{ width: COL_W, height: CANVAS_H + 27 }}>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Finals
            </p>
            <div className="relative flex-1">
              <div className="absolute left-0 right-0" style={{ top: finalCy - MATCH_H / 2 }}>
                <PredictionMatchup
                  {...final}
                  pickedId={championId}
                  onPick={(teamId) => handlePick(4, 0, teamId)}
                />
                <div className="mt-6">
                  <ChampionBanner team={champion} />
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
    </div>
  );
};